import React, {
  ReactElement,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl, getApiBaseUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import CompaniesForm from "./CompaniesForm";
import {
  Pencil,
  Trash,
  Ellipsis,
  Warehouse,
  Building,
  Eraser,
} from "lucide-react";
import { Company } from "@/types";
import { useTranslation } from "react-i18next";
import { DeleteModal } from "@/components/delete-modal";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { companyAtom } from "@/store/company";
import { useAtom } from "jotai";
import CategoriesForm from "./CategoriesForm";
import { toast } from "react-toastify";
import { PageHeader } from "@/components/PageHeader";
import { debounce } from "lodash";
import { Input } from "@/components/ui/input";
import ComapaniesIcon from "@/components/icons/CompaniesIcon";

interface ApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Company[];
}

export default function CompaniesList(): ReactElement {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCategoriesDrawerOpen, setIsCategoriesDrawerOpen] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState<
    number | undefined
  >();
  const [companyToDelete, setCompanyToDelete] = useState<number | null>(null);
  const [_, setCompany] = useAtom(companyAtom);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [vat, setVat] = useState("");

  // WebSocket connection
  const socketRef = useRef<WebSocket | null>(null);
  const wsInstanceIdRef = useRef<number>(0); // To generate unique IDs for socket instances

  const debouncedSetName = useMemo(
    () =>
      debounce((value) => {
        setName(value);
      }, 300),
    [],
  );

  const debouncedSetEmail = useMemo(
    () =>
      debounce((value) => {
        setEmail(value);
      }, 300),
    [],
  );

  const debouncedSetVat = useMemo(
    () =>
      debounce((value) => {
        setVat(value);
      }, 300),
    [],
  );

  useEffect(() => {
    if (!token) {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      return;
    }

    if (socketRef.current) {
      return;
    }

    wsInstanceIdRef.current += 1;
    const currentWsId = wsInstanceIdRef.current;

    let wsUrl = "";
    const apiBase = getApiBaseUrl();

    if (apiBase.startsWith("http")) {
      try {
        const apiUrlObject = new URL(apiBase);
        const wsProtocol = apiUrlObject.protocol === "https:" ? "wss:" : "ws:";
        wsUrl = `${wsProtocol}//${apiUrlObject.host}/ws/notifications/`;
      } catch (e) {
        return;
      }
    } else {
      const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      wsUrl = `${wsProtocol}//${window.location.host}/ws/notifications/`;
    }

    const socket = new WebSocket(wsUrl);
    socketRef.current = socket; // Assign early to prevent re-connection attempts by this effect run

    socket.onopen = () => {
      if (socketRef.current === socket) {
        // WebSocket connected.
      } else {
        socket.close(); // Close the stale connection
      }
    };

    socket.onmessage = (event) => {
      if (socketRef.current !== socket) {
        return;
      }
      try {
        const parsedEventData = JSON.parse(event.data);
        // The actual company list is in parsedEventData.message.data
        if (
          parsedEventData.type === "global" &&
          parsedEventData.payload &&
          parsedEventData.payload.event_type === "company_list_update"
        ) {
          const companies = parsedEventData.payload.data;
          // Update local state, conforming to ApiResponse structure
          setData({
            results: companies,
            count: companies.length,
            next: null,
            previous: null,
          });
          // You might want a specific toast for this update, e.g.:
          // toast.info(t("notifications.websocket.companyListUpdated"));
          // For now, let's keep it general or remove if not needed for list updates
        } else if (parsedEventData.type === "notification") {
          // Handle other specific notification types if necessary
          toast.success(
            parsedEventData.payload?.message ||
              t("notifications.websocket.defaultNotification"),
          );
        } else {
          // Generic global message handling (if any other than company_list_update)
          // toast.info(t("notifications.websocket.globalMessageReceived"));
        }
      } catch (error) {
        // Optionally, add a user-facing error toast here if needed
      }
    };

    socket.onerror = (event) => {
      if (socketRef.current !== socket) {
        return;
      }
    };

    socket.onclose = (event) => {
      // Ensure we are cleaning up the correct socket instance
      if (socketRef.current === socket) {
        socketRef.current = null; // Clear the ref if this socket is closing
      }
    };

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [token, toast]); // Added toast to dependencies as it's used in the effect

  const {
    data: companiesData,
    isLoading,
    error,
  } = useQuery<ApiResponse>({
    queryKey: ["companies", name, email, vat],
    queryFn: async () => {
      const params = new URLSearchParams({
        name,
        email,
        vat,
      });

      if (name) params.append("name", name);
      if (email) params.append("email", email);
      if (vat) params.append("vat", vat);

      const response = await fetch(
        getApiUrl(`companies/?${params.toString()}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch companies");
      }
      return response.json();
    },
  });

  useEffect(() => {
    if (companiesData) {
      setData(companiesData);
    }
  }, [companiesData]);

  const deleteMutation = useMutation({
    mutationFn: async (companyId: number) => {
      const response = await fetch(getApiUrl(`companies/${companyId}/`), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete company");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      toast.success(t("companies.deleteSuccess")); // Assuming you have a translation key
      setCompanyToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || t("companies.deleteError")); // Assuming a fallback translation
      setCompanyToDelete(null);
    },
  });

  const handleAdd = () => {
    setEditingCompanyId(undefined);
    setIsDrawerOpen(true);
  };

  const handleEdit = (companyId: number) => {
    setEditingCompanyId(companyId);
    setIsDrawerOpen(true);
  };

  const handleDetails = (companyId: number) => {
    setCompany({ id: companyId, name: "" });
    navigate(`/facilities`);
  };

  const handleDeleteClick = (companyId: number) => {
    setCompanyToDelete(companyId);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["companies"] });
    setIsDrawerOpen(false);
    setIsCategoriesDrawerOpen(false);
  };

  const handleClearFilters = () => {
    const nameInput = document.getElementById(
      "name-filter",
    ) as HTMLInputElement;
    const emailInput = document.getElementById(
      "email-filter",
    ) as HTMLInputElement;
    const vatInput = document.getElementById("vat-filter") as HTMLInputElement;
    if (nameInput) nameInput.value = "";
    if (emailInput) emailInput.value = "";
    if (vatInput) vatInput.value = "";
    setName("");
    setEmail("");
    setVat("");
  };

  if (error) {
    return (
      <div className="text-destructive">Error: {(error as Error).message}</div>
    );
  }

  return (
    <div>
      {/* <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold">{t("companies.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("companies.description")}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          {user?.rol === "admin" && (
            <Button variant="default" onClick={handleAdd}>
              {t("companies.add")}
            </Button>
          )}
        </div>
      </div> */}

      <PageHeader
        icon={
          <ComapaniesIcon
            width={180}
            height={180}
            className="w-[50px] h-[50px]"
          />
        }
        actionTitle="companies.add"
        title="companies.title"
        description="companies.description"
        handleNew={handleAdd}
      />

      <div className="flex items-center space-x-2 p-4 mt-8">
        <Input
          id="name-filter"
          placeholder={t("filters.filterByName")}
          onChange={(e) => debouncedSetName(e.target.value)}
          className="max-w-sm"
        />
        <Input
          id="email-filter"
          placeholder={t("filters.filterByEmail")}
          onChange={(e) => debouncedSetEmail(e.target.value)}
          className="max-w-sm"
        />
        <Input
          id="vat-filter"
          placeholder={t("filters.filterByVat")}
          onChange={(e) => debouncedSetVat(e.target.value)}
          className="max-w-sm"
        />
        <Button
          onClick={handleClearFilters}
          variant="default"
          className="bg-primary text-primary-foreground hover:bg-primary-foreground/50 hover:text-primary-foreground"
        >
          <Eraser className="h-4 w-4" />
        </Button>
      </div>

      <div className="px-4">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <div className="p-6">
                  <Skeleton className="h-12 w-12 rounded-full mb-4" />
                  <Skeleton className="h-5 w-[180px] mb-2" />
                  <Skeleton className="h-4 w-[150px] mb-1" />
                  <Skeleton className="h-4 w-[120px] mb-1" />
                  <Skeleton className="h-4 w-[100px]" />
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {data?.results.map((company) => (
              <Card
                key={company.id}
                className="overflow-hidden hover:cursor-pointer hover:bg-muted transform hover:scale-105 transition duration-300"
                onClick={() => handleDetails(company.id)}
              >
                <CardHeader className="relative pb-0">
                  <div className="absolute right-4 top-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Ellipsis className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleEdit(company.id);
                          }}
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          {t("common.edit")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setEditingCompanyId(company.id);
                            setIsCategoriesDrawerOpen(true);
                          }}
                        >
                          <Warehouse className="h-4 w-4 mr-2" />
                          {t("common.categories")}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleDeleteClick(company.id);
                          }}
                          className="text-destructive"
                        >
                          <Trash className="h-4 w-4 mr-2" />
                          {t("common.delete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full overflow-hidden bg-accent flex items-center justify-center">
                      {company.logo ? (
                        <img
                          src={company.logo}
                          alt={company.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-lg font-medium">
                          {company.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium">{company.name}</h3>
                      <Badge
                        variant={company.is_active ? "default" : "secondary"}
                        className="mt-1"
                      >
                        {company.is_active
                          ? t("common.active")
                          : t("common.inactive")}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {company.vat && (
                    <p className="text-sm text-muted-foreground mb-1">
                      {company.vat}
                    </p>
                  )}
                  {company.email && (
                    <p className="text-sm text-muted-foreground mb-1">
                      {company.email}
                    </p>
                  )}
                  {company.phone && (
                    <p className="text-sm text-muted-foreground mb-1">
                      {company.phone}
                    </p>
                  )}
                  {company.mobile && (
                    <p className="text-sm text-muted-foreground">
                      {company.mobile}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Company edit form */}
      <Drawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <DrawerHeader>
            <DrawerTitle>
              {editingCompanyId ? t("companies.edit") : t("companies.add")}
            </DrawerTitle>
            <DrawerDescription>
              {editingCompanyId
                ? t("companies.edit-description")
                : t("companies.add-description")}
            </DrawerDescription>
          </DrawerHeader>
          <CompaniesForm
            companyId={editingCompanyId}
            onSuccess={handleSuccess}
            onCancel={() => setIsDrawerOpen(false)}
          />
        </DrawerContent>
      </Drawer>

      {/* facility categories form */}
      <Drawer
        open={isCategoriesDrawerOpen}
        onOpenChange={setIsCategoriesDrawerOpen}
        direction="right"
      >
        <DrawerContent className="h-full w-[500px] ml-auto">
          <DrawerHeader>
            <DrawerTitle>
              {editingCompanyId ? t("companies.edit") : t("companies.add")}
            </DrawerTitle>
            <DrawerDescription>
              {editingCompanyId
                ? t("companies.edit-description")
                : t("companies.add-description")}
            </DrawerDescription>
          </DrawerHeader>
          <CategoriesForm
            companyId={editingCompanyId}
            onSuccess={handleSuccess}
            onCancel={() => setIsCategoriesDrawerOpen(false)}
          />
        </DrawerContent>
      </Drawer>

      {/* Delete confirmation modal */}
      <DeleteModal
        open={companyToDelete !== null}
        onOpenChange={(open) => !open && setCompanyToDelete(null)}
        entity={t("companies.title", "company").toLowerCase()}
        onDelete={() => deleteMutation.mutate(companyToDelete!)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
